/**
 * Rule-group / capacity-rule card body: conditions as short prose rows,
 * action chips (weight payload spelled out), capacity floor/ceiling line.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { CalendarRange } from 'lucide-react';
import type { RuleAction, RuleNode } from '@trafficpulse/shared';

import { RULE_ACTION_META } from '@/features/flow-optimizer/tabs/exclude/rule-action-meta';

import type { ReviewItem } from '../validate/review-items';

function conditionText(node: RuleNode): string {
  switch (node.kind) {
    case 'condition': {
      const ct = node.compareTo;
      if (!ct) return `${node.metric} ${node.op} ${node.value}`;
      const win = ct.dateRange ? ` (${ct.dateRange})` : '';
      const pct = ct.percent === undefined ? '' : `${ct.percent}% of `;
      const src = ct.against === 'total' ? 'TOTAL ' : '';
      return `${node.metric} ${node.op} ${pct}${src}${ct.metric}${win}`;
    }
    case 'ranking':
      return `${node.metric} is ${node.op.replace('_', ' ')}${
        node.percent ? ` ${node.percent}%` : ''
      }`;
    case 'tag':
      return `${node.negate ? 'NOT ' : ''}tag "${node.tagName}"`;
    case 'not':
      return `NOT (${conditionText(node.child)})`;
    case 'group':
      return `(${node.children.map(conditionText).join(` ${node.op} `)})`;
  }
}

const ReviewRuleBody: FC<{ item: ReviewItem }> = ({ item }) => {
  if (item.capacityRule) {
    const rule = item.capacityRule;
    return (
      <p className="text-[11px] text-muted-foreground">
        {rule.layers.join(', ')} — keep at most{' '}
        <span className="font-semibold text-foreground">{rule.max ?? '∞'}</span>{' '}
        live{rule.min != null ? `, at least ${rule.min}` : ''}.
      </p>
    );
  }
  const group = item.ruleGroup;
  if (!group || group.kind !== 'group') return null;
  const window =
    group.dateRange === 'custom'
      ? `custom ${group.dateRangeCustomFrom ?? '?'} → ${group.dateRangeCustomTo ?? '?'}`
      : group.dateRange;
  return (
    <div className="space-y-1">
      <p className="truncate text-[11px] text-muted-foreground">
        {group.children.map(conditionText).join(` ${group.op} `) || 'no conditions'}
      </p>
      <p className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
        <CalendarRange className="h-3 w-3" aria-hidden />
        {window ?? 'window inherited — the formula group’s (or last-7-days)'}
      </p>
      <div className="flex flex-wrap gap-1">
        {(group.actions ?? ['exclude']).map((action) => {
          const meta = RULE_ACTION_META[action as RuleAction];
          const Icon = meta?.Icon;
          return (
            <span
              key={action}
              className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                meta?.tone ?? 'bg-muted text-muted-foreground'
              }`}
            >
              {Icon ? <Icon className="h-3 w-3" aria-hidden /> : null}
              {action === 'weight' && group.weight
                ? `${meta?.label ?? 'Weight'} · ${group.weight.mode} ${group.weight.percent}%`
                : (meta?.label ?? action)}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default ReviewRuleBody;
